interface Config {
  api: {
    baseUrl: string;
    endpoints: {
      products: string;
      checkout: string;
      contact: string;
    };
  };
  app: {
    name: string;
    version: string;
    environment: string;
  };
  features: {
    stripe: boolean;
    contactForm: boolean;
    analytics: boolean;
  };
  stripe: {
    publicKey: string;
  };
  debug: {
    enabled: boolean;
    logLevel: string;
  };
  // Environment-specific Lambda function names
  lambdaFunctions: {
    getProducts: string;
    processPayment: string;
  };
}

// Get environment variables with fallbacks
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;
const isTest = import.meta.env.MODE === 'test';

// Import environment-specific configs
import { config as testConfig } from './config.test';
import { config as prodConfig } from './config.prod';

// API base URL - automatically detected based on environment
const getApiBaseUrl = (): string => {
  if (isDevelopment) {
    return 'http://localhost:5000';
  }
  if (isTest) {
    // Use test config for test environment
    return testConfig.apiBaseUrl;
  }
  if (isProduction) {
    // Use production config for production environment
    return prodConfig.apiBaseUrl;
  }
  return 'http://localhost:5000'; // Default fallback
};

export const config: Config = {
  api: {
    baseUrl: getApiBaseUrl(),
    endpoints: {
      products: '/api/products',
      checkout: '/api/checkout',
      contact: '/api/contact',
    },
  },
  app: {
    name: import.meta.env.VITE_APP_NAME || "Michael's Website",
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: isDevelopment
      ? 'development'
      : isProduction
      ? 'production'
      : 'unknown',
  },
  features: {
    stripe: import.meta.env.VITE_ENABLE_STRIPE !== 'false',
    contactForm: import.meta.env.VITE_ENABLE_CONTACT_FORM !== 'false',
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  },
  stripe: {
    publicKey: (() => {
      // Use the live key from environment variables in production
      if (isProduction) {
        return import.meta.env.VITE_STRIPE_LIVE_PUBLIC_KEY || 'pk_live_51S0YrIPyiM7Fj0JorTU3peJt3d0jqwApLCumGmZ3S4ajfSm5GiAVPlYTkeVagODQKQSgbJZcMgESYKI3NHq2lURw00hGK2KfeP';
      }
      // For test environment
      if (isTest) {
        return 'pk_test_51S0YrIPyiM7Fj0JorTU3peJt3d0jqwApLCumGmZ3S4ajfSm5GiAVPlYTkeVagODQKQSgbJZcMgESYKI3NHq2lURw00hGK2KfeP';
      }
      // For development, use the test key from environment variables or fallback
      return (
        import.meta.env.VITE_STRIPE_PUBLIC_KEY ||
        'pk_test_51S0YrRLFv62eRi6FcbfWlVQrhngssWcH7n0HAMPdbYJKpyROSM8REeAxrrh6JxQrV91hvqOXma98yeRkUI6mceoX00ulvinGvD'
      );
    })(),
  },
  debug: {
    enabled: import.meta.env.VITE_DEBUG_MODE === 'true' || isDevelopment,
    logLevel:
      import.meta.env.VITE_LOG_LEVEL || (isDevelopment ? 'debug' : 'info'),
  },
  // Environment-specific Lambda function names
  lambdaFunctions: {
    getProducts: isTest
      ? 'michael-website-test-get-products'
      : 'michael-website-prod-get-products',
    processPayment: isTest
      ? 'michael-website-test-process-payment'
      : 'michael-website-prod-process-payment',
  },
};

// Helper functions for API calls
export const getApiUrl = (endpoint: string): string => {
  return `${config.api.baseUrl}${endpoint}`;
};

export const getProductApiUrl = (id: string): string => {
  return `${config.api.baseUrl}/api/products/${id}`;
};

export const getCheckoutApiUrl = (): string => {
  return `${config.api.baseUrl}/api/checkout`;
};

export const getContactApiUrl = (): string => {
  return `${config.api.baseUrl}/api/contact`;
};

export default config;
